import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import TrendElevated from '@/assets/icons/trend_elevated.svg?component';
import TrendNormal from '@/assets/icons/trend_normal.svg?component';
import Tooltip from '@/components/atoms/Tooltip';
import MetricCard, {
    type MetricCardBadge,
    type MetricCardIcon,
    type MetricTone,
} from '@/components/molecules/MetricCard/MetricCard';

import type { HealthWoundsMetricTrendType } from '../../_utils/healthWoundsMetricTrend';

interface HealthWoundsMetricCardProps {
    title: string;
    value: string | number | ReactNode;
    isLoading?: boolean;
    icon: MetricCardIcon;
    tone: MetricTone;
    iconCustomSize?: number;
    trendType?: HealthWoundsMetricTrendType;
    tooltipContent?: string;
    tooltipWidth?: number;
}

/** `MetricCard` plus the wound trend pill and the module's own tooltip. */
export default function HealthWoundsMetricCard(props: HealthWoundsMetricCardProps) {
    const {
        title,
        value,
        isLoading = false,
        icon,
        tone,
        iconCustomSize,
        trendType = 'none',
        tooltipContent,
        tooltipWidth = 450,
    } = props;
    const { t } = useTranslation('healthModule');

    const badge: MetricCardBadge | undefined =
        trendType === 'elevated'
            ? { icon: TrendElevated, label: t('dashboard.alertIncreasing'), tone: 'danger' }
            : trendType === 'reduced'
              ? { icon: TrendNormal, label: t('wounds.trendReduced'), tone: 'success' }
              : trendType === 'normal'
                ? { icon: TrendNormal, label: t('dashboard.alertStable'), tone: 'success' }
                : undefined;

    const card = (
        <MetricCard
            title={title}
            value={value}
            isLoading={isLoading}
            icon={icon}
            tone={tone}
            iconCustomSize={iconCustomSize}
            badge={badge}
        />
    );

    if (tooltipContent) {
        return (
            <Tooltip content={tooltipContent} placement="top" maxWidth={tooltipWidth}>
                {card}
            </Tooltip>
        );
    }

    return card;
}
